import { FC, useCallback } from 'react';
import { User } from '../../../types/User';
import { usePopup } from '../../../context/PopupProvider';
import {
    DefaultFooterComponent,
    PopupFooterProps,
    PopupStateProps,
    showAlert,
    TextInput
} from '@silverfort/sf-components';
import { useUpdateUsers } from '../../../api';

interface UseSetMaximumTokensPopupParams {
    users: User[];
    onMaxTokensSet?: () => void;
}

interface ReturnType {
    openSetMaximumTokensPopup: () => void;
}

interface MaxTokensPopupState {
    maxTokens: number | null;
}

type MaxTokensContentProps = PopupStateProps<MaxTokensPopupState>;
type MaxTokensFooterProps = PopupFooterProps<MaxTokensPopupState>;

const isValidNumber = (value: string) => /^\d+$/.test(value);

const getInitialMaxTokens = (users: User[]): number | null => {
    if (!users.length) {
        return null;
    }
    const tokenLimitValues = new Set(users.map((u) => u.tokenLimit));
    const isAllLimitEqual = tokenLimitValues.size === 1;
    const initialMaxTokens = isAllLimitEqual ? [...tokenLimitValues][0] : null;

    return initialMaxTokens;
};

const MaxTokensContent: FC<MaxTokensContentProps> = ({ popupState, setPopupState }) => {
    const { maxTokens } = popupState;

    const handleChange = (text: string) => {
        if (text === '') {
            setPopupState({ maxTokens: null });
        } else if (isValidNumber(text)) {
            setPopupState({ maxTokens: Number(text) });
        }
    };

    return (
        <div className="flex flex-col flex-1">
            <TextInput
                className="w-full"
                type="number"
                value={maxTokens ?? ''}
                onChange={handleChange}
                placeholder="Enter number of max tokens"
            />
        </div>
    );
};

const MaxTokensFooter: FC<MaxTokensFooterProps> = ({ popupState, ...props }) => {
    const { maxTokens } = popupState ?? {};
    const isSaveButtonDisabled = maxTokens == null || maxTokens <= 0;

    return (
        <DefaultFooterComponent
            primaryButtonProps={{
                text: 'Save',
                disabled: isSaveButtonDisabled
            }}
            {...props}
        />
    );
};

export const useSetMaximumTokensPopup = ({
    users,
    onMaxTokensSet
}: UseSetMaximumTokensPopupParams): ReturnType => {
    const { openPopup, closePopup } = usePopup();

    const handleSuccess = useCallback(() => {
        closePopup();
        onMaxTokensSet?.();
        showAlert('To revert go to settings', 'success', { title: 'Max tokens set successfully' });
    }, [closePopup, onMaxTokensSet]);

    const handleError = useCallback(() => {
        showAlert('Failed to set maximum tokens, please try again', 'error');
    }, []);

    const { updateUsers } = useUpdateUsers({
        onSuccess: handleSuccess,
        onError: handleError
    });

    const handleSave = (state?: MaxTokensPopupState) => {
        if (!state || state.maxTokens == null || state.maxTokens <= 0) {
            showAlert('Please enter a valid number', 'error');
            return;
        }

        const { maxTokens } = state;

        updateUsers(users.map(({ guid }) => ({ guid, tokenLimit: maxTokens })));
    };

    const openSetMaximumTokensPopup = () => {
        openPopup({
            title: 'Set Maximum Tokens',
            popupClassName: 'w-[360px]',
            ContentComponent: MaxTokensContent,
            FooterComponent: MaxTokensFooter,
            initialState: { maxTokens: getInitialMaxTokens(users) },
            onApprove: handleSave
        });
    };

    return {
        openSetMaximumTokensPopup
    };
};
